import './MarketPerformanceTable.scss';
import cn from 'classnames';
import { useMemo, useState } from 'react';
import { InteractiveSpan } from '@Components/InteractiveEl';
import { DivergentColorMap20, makeSteppedHeatMap } from '@src/utils/colors';
import { efficiencyOf, MarketSummary, MarketTotals } from '../utils/dtc-commerce-mmm-markets';
import {
  formatCurrency,
  formatDollarsExact,
  formatPercentage,
  formatWholeNumber
} from '../utils/dtc-commerce-mmm-format';
import {
  MarketSortColumn,
  MarketSortColumnType,
  SortDirection,
  SortDirectionType
} from './market-performance-table-constants';
import { buildMarketTableRows, sortMarketRows } from './market-performance-table-utils';
import { PerformanceTableShell } from './PerformanceTableShell';

const formatShare = (share: number | null): string =>
  share === null ? '—' : formatPercentage(share * 100, 1);

interface MarketPerformanceTableProps {
  markets: MarketSummary[];
  total: MarketTotals;
  isRevenue: boolean;
  dmaNames?: Record<string, string>;
}

export const MarketPerformanceTable = ({
  markets,
  total,
  isRevenue,
  dmaNames
}: MarketPerformanceTableProps) => {
  const [sortColumn, setSortColumn] = useState<MarketSortColumnType>(MarketSortColumn.SPEND);
  const [sortDir, setSortDir] = useState<SortDirectionType>(SortDirection.DESC);

  const rows = useMemo(
    () => buildMarketTableRows(markets, isRevenue, dmaNames),
    [markets, dmaNames, isRevenue]
  );

  const toggleSort = (col: MarketSortColumnType) => {
    if (sortColumn === col) {
      setSortDir(prev => (prev === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC));
      return;
    }
    setSortColumn(col);
    setSortDir(col === MarketSortColumn.MARKET ? SortDirection.ASC : SortDirection.DESC);
  };

  const sorted = useMemo(
    () => sortMarketRows(rows, sortColumn, sortDir, isRevenue),
    [rows, sortColumn, sortDir, isRevenue]
  );

  const totalEfficiency = efficiencyOf(total.spend, total.contribution, isRevenue);

  // Midpoint at the total's ROAS or CPA, so a cell reads as better or worse than the business
  // as a whole, as Channel Performance's efficiency column does.
  const efficiencyHeatMap = useMemo(() => {
    const values = rows
      .map(r => r.efficiency)
      .filter((v): v is number => v !== null && Number.isFinite(v));
    if (values.length < 2) return null;
    const min = Math.min(...values);
    const max = Math.max(...values);
    if (min === max) return null;
    const midpoint =
      totalEfficiency !== null ? Math.max(min, Math.min(max, totalEfficiency)) : undefined;
    return makeSteppedHeatMap({
      min,
      max,
      midpoint,
      stops: isRevenue ? DivergentColorMap20 : [...DivergentColorMap20].reverse()
    });
  }, [rows, totalEfficiency, isRevenue]);

  const kpiLabel = isRevenue ? 'Revenue' : 'Conv.';
  const efficiencyLabel = isRevenue ? 'ROAS' : 'CPA';
  const formatEffect = (value: number): string =>
    isRevenue ? formatCurrency(value) : formatWholeNumber(value);

  const headers: { col: MarketSortColumnType; label: string }[] = [
    { col: MarketSortColumn.SPEND, label: 'Spend' },
    { col: MarketSortColumn.SPEND_PCT, label: 'Spend %' },
    { col: MarketSortColumn.EFFECT, label: kpiLabel },
    { col: MarketSortColumn.EFFECT_PCT, label: `${kpiLabel} %` },
    { col: MarketSortColumn.EFFICIENCY, label: efficiencyLabel }
  ];

  const note = (
    <p className='market-performance-table-note'>
      Returns are averages over the selected dates: what happened, not where the next dollar would
      do the most.
    </p>
  );

  return (
    <PerformanceTableShell className='market-performance-table' belowTable={note}>
      <colgroup>
        <col className='market-name-column' />
      </colgroup>
      <thead>
        <tr>
          <th className={cn('align-left', { active: sortColumn === MarketSortColumn.MARKET })}>
            <InteractiveSpan onClick={() => toggleSort(MarketSortColumn.MARKET)}>
              Market
              {sortColumn === MarketSortColumn.MARKET &&
                (sortDir === SortDirection.DESC ? ' ↓' : ' ↑')}
            </InteractiveSpan>
          </th>
          {headers.map(h => {
            const active = sortColumn === h.col;
            return (
              <th key={h.col} className={cn('align-center', { active })}>
                <InteractiveSpan onClick={() => toggleSort(h.col)}>
                  {h.label}
                  {active && (sortDir === SortDirection.DESC ? ' ↓' : ' ↑')}
                </InteractiveSpan>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {sorted.map(row => (
          <tr key={row.geo} className={cn({ 'pooled-market-row': row.isPooled })}>
            <td className='align-left'>
              <span className='market-name'>{row.displayName}</span>
            </td>
            <td className='align-center tabular'>
              <MetricCell value={formatCurrency(row.spend)} />
            </td>
            <td className='align-center tabular'>
              <MetricCell value={formatShare(row.spendShare)} />
            </td>
            <td className='align-center tabular'>
              <MetricCell value={formatEffect(row.contribution)} />
            </td>
            <td className='align-center tabular'>
              <MetricCell value={formatShare(row.contributionShare)} />
            </td>
            <td
              className='align-center tabular'
              style={
                efficiencyHeatMap && row.efficiency !== null && Number.isFinite(row.efficiency)
                  ? { backgroundColor: efficiencyHeatMap(row.efficiency) }
                  : undefined
              }
            >
              <MetricCell value={formatDollarsExact(row.efficiency)} />
            </td>
          </tr>
        ))}
        {sorted.length === 0 && (
          <tr>
            <td colSpan={headers.length + 1} className='channel-performance-table-empty'>
              No markets in the selected window.
            </td>
          </tr>
        )}
      </tbody>
      <tfoot>
        <tr>
          <td className='align-left'>Total</td>
          <td className='align-center tabular'>
            <MetricCell value={formatCurrency(total.spend)} />
          </td>
          <td aria-hidden='true' />
          <td className='align-center tabular'>
            <MetricCell value={formatEffect(total.contribution)} />
          </td>
          <td aria-hidden='true' />
          <td className='align-center tabular'>
            <MetricCell value={formatDollarsExact(totalEfficiency)} />
          </td>
        </tr>
      </tfoot>
    </PerformanceTableShell>
  );
};

interface MetricCellProps {
  value: string;
}

const MetricCell = ({ value }: MetricCellProps) => (
  <span className='metric-cell'>
    <span className='metric-value'>{value}</span>
  </span>
);
